import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useTranslation } from 'react-i18next';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filter: string, ascend: boolean) => void;
}

export default function FilterModal({ isOpen, onClose, onApply }: FilterModalProps) {
  const { t } = useTranslation(['modal', 'common']);
  // Get current filter settings from Redux (same as mobile app)
  const { filter: currentFilter, ascend: currentAscend } = useSelector((state: RootState) => state.auth);
  
  // Local state for the modal
  const [filterValue, setFilterValue] = useState<string>(currentFilter || 'id');
  const [orderValue, setOrderValue] = useState<boolean>(currentAscend !== null ? currentAscend : true);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isOrderDropdownOpen, setIsOrderDropdownOpen] = useState(false);

  // Filter options (same as mobile app)
  const filterOptions = [
    { label: t('modal:id'), value: 'id' },
    { label: t('modal:name'), value: 'name' },
    { label: t('modal:building'), value: 'building' },
    { label: t('modal:location'), value: 'location' },
    { label: t('modal:favorites'), value: 'favorite' },
  ];

  // Order options (same as mobile app)
  const orderOptions = [
    { label: t('modal:ascending'), value: true },
    { label: t('modal:descending'), value: false },
  ];

  // Update local state when Redux state changes
  useEffect(() => {
    setFilterValue(currentFilter || 'id');
    setOrderValue(currentAscend !== null ? currentAscend : true);
  }, [currentFilter, currentAscend]);

  const handleApply = () => {
    onApply(filterValue, orderValue);
    onClose();
  };

  const handleCancel = () => {
    // Reset to current values (same as mobile app)
    setFilterValue(currentFilter || 'id');
    setOrderValue(currentAscend !== null ? currentAscend : true);
    setIsFilterDropdownOpen(false);
    setIsOrderDropdownOpen(false);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw] shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">{t('modal:filterOptions')}</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-900 cursor-pointer"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filter Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('modal:filterBy')}
          </label>
          <div className="relative">
            <button
              onClick={() => {
                setIsFilterDropdownOpen(!isFilterDropdownOpen);
                setIsOrderDropdownOpen(false);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <span className="block truncate">
                {filterOptions.find(option => option.value === filterValue)?.label || 'Select...'}
              </span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
            
            {isFilterDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                {filterOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setFilterValue(option.value);
                      setIsFilterDropdownOpen(false);
                    }}
                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${
                      filterValue === option.value ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                    }`}
                  >
                    <span className="block truncate font-normal">{option.label}</span>
                    {filterValue === option.value && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Order Selection */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('modal:sortBy')}
          </label>
          <div className="relative">
            <button
              onClick={() => {
                setIsOrderDropdownOpen(!isOrderDropdownOpen);
                setIsFilterDropdownOpen(false);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <span className="block truncate">
                {orderOptions.find(option => option.value === orderValue)?.label || 'Select...'}
              </span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
            
            {isOrderDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                {orderOptions.map((option) => (
                  <div
                    key={option.value.toString()}
                    onClick={() => {
                      setOrderValue(option.value);
                      setIsOrderDropdownOpen(false);
                    }}
                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${
                      orderValue === option.value ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                    }`}
                  >
                    <span className="block truncate font-normal">{option.label}</span>
                    {orderValue === option.value && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-900 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {t('modal:apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
